import {Component, OnDestroy} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {IncidentApiService, IncidentPriorityType, IncidentsResponse, IncidentType} from '@npck/af-frontend-api';
import {DATE_TIME_SERVER_FORMAT, RouterService, rxSpinner, TAB} from '@npck/libs/commons';
import dayjs from 'dayjs';
import {Subscription} from 'rxjs';
import {
    fieldExtractor,
    FilterDictionaryField,
    FilterEventForm,
    FilterField,
    getFormattedDateOrNull,
} from '../../components/full-table/filter/filter.component';
import {mergeTableEvent, TableEvent} from '../../components/full-table/full-table.component';
import {
    Column,
    ColumnType,
    ELLIPSIS_LARGE_TEXT_MAX_LENGTH,
    ELLIPSIS_MEDIUM_TEXT_MAX_LENGTH,
    Row,
    StatusColumn,
} from '../../components/full-table/table/table.component';
import {mapIncidentStatusToStyle} from '../../components/status/status.component';
import {CACHE_EXPIRATION_INTERVAL} from '../../domain/constants';
import {IncidentFilterHolderService} from '../../domain/incident-filter-holder.service';
import {Path} from '../../path';


@Component({
    templateUrl: 'incidents.component.html',
    standalone: false,
})
export class IncidentsComponent implements OnDestroy {
    tabs: IncidentsTab[] = ['tabAllIncidents', 'tabMyIncidents', 'tabRelatedIncidents', 'tabOrganizationsIncidents'];
    activeTab: IncidentsTab;
    selectedTabIndex: number;
    showCreateButton: boolean = true;
    chips?: string[];
    chipsFormControlName?: keyof FilterEventForm;
    commonFilters: FilterField[] = [
        {
            type: 'text',
            name: 'query',
        },
        <FilterDictionaryField>{
            isMultiSelect: true,
            type: 'select',
            name: 'incidentPriority',
            dictionaryName: 'incident_priority',
            values: [],
        },
        <FilterDictionaryField>{
            isMultiSelect: true,
            type: 'select',
            name: 'incidentResolution',
            dictionaryName: 'incident_status',
            filterBy: ['resolution'],
            values: [],
        },
        <FilterDictionaryField>{
            isMultiSelect: true,
            type: 'select',
            name: 'incidentStatus',
            dictionaryName: 'incident_status',
            filterBy: ['status'],
            values: [],
        },
        <FilterDictionaryField>{
            isMultiSelect: true,
            type: 'select',
            name: 'incidentFraudTypes',
            dictionaryName: 'fraud_type',
            values: [],
        },
        <FilterDictionaryField>{
            isMultiSelect: true,
            type: 'select',
            name: 'incidentTransactionTypes',
            dictionaryName: 'transaction_type',
            values: [],
        },
        <FilterDictionaryField>{
            isMultiSelect: true,
            type: 'select',
            name: 'incidentTransactionStatuses',
            dictionaryName: 'transaction_status',
            values: [],
        },
        <FilterDictionaryField>{
            isMultiSelect: true,
            type: 'select',
            name: 'sourceOrganizations',
            dictionaryName: 'bin',
            values: [],
        },
        <FilterDictionaryField>{
            isMultiSelect: true,
            type: 'select',
            name: 'incidentRelatedOrganizations',
            dictionaryName: 'bin',
            filterBy: ['BANK', 'PAYM', 'MFO', 'OPERATOR', 'MOBILE_OPERATOR'],
            values: [],
        },
        <FilterDictionaryField>{
            isMultiSelect: true,
            type: 'select',
            name: 'incidentGovernment',
            dictionaryName: 'bin',
            filterBy: ['IO', 'GOV'],
            values: [],
        },
        <FilterField>{
            type: 'dateRange',
            name: 'separatedDateRange',
        },
        <FilterField>{
            type: 'amountRange',
            name: 'amountRange',
        },
        <FilterField>{
            type: 'accountBalanceRange',
            name: 'accountBalanceRange',
        },
    ];
    filterFields: FilterField[] = [];
    dataSource: Row[] = [];

    private incidentsSubscription: Subscription;

    constructor(
        public incidentFilterHolderService: IncidentFilterHolderService,
        private routerService: RouterService,
        private activatedRoute: ActivatedRoute,
        private incidentApiService: IncidentApiService,
    ) {
        this.activeTab = this.activatedRoute.snapshot.queryParamMap.get(TAB) as IncidentsTab || 'tabAllIncidents';
        this.selectedTabIndex = this.tabs.findIndex(tab => tab == this.activeTab);
        if (this.selectedTabIndex === -1) {
            this.activeTab = this.tabs[0];
            this.selectedTabIndex = 0;
        }
        this.handleNavigation(this.selectedTabIndex);
    }

    ngOnDestroy(): void {
        this.incidentsSubscription?.unsubscribe();
    }

    handleNavigation($event: number): void {
        this.activeTab = this.tabs[$event];
        this.routerService.navigate(
            Path.INCIDENTS,
            {
                queryParams: {[TAB]: this.activeTab},
                queryParamsHandling: 'merge',
            });
        this.filterFields = [...this.commonFilters];
        const chips = this.activeTab === 'tabAllIncidents' ?
            [IncidentType.FRAUD, IncidentType.MSISDN_FRAUD, IncidentType.COMPROMISED_PAYMENT_INSTRUMENT, IncidentType.OTHER] : [];
        this.chips = chips.length ? chips : undefined;
        this.chipsFormControlName = chips.length ? 'incidentType' : undefined;
        this.loadIncidents(this.incidentFilterHolderService.getState(this.activeTab).tableEvent);
    }

    navigateToCreateIncident(): void {
        this.routerService.navigateToTab<IncidentsTab>(Path.INCIDENTS_CREATE, this.activeTab);
    }

    loadIncidents(tableEvent: TableEvent): void {
        this.incidentsSubscription?.unsubscribe();

        const mergedEvent = mergeTableEvent(this.incidentFilterHolderService.getState(this.activeTab).tableEvent, tableEvent);
        const activeTabState = this.incidentFilterHolderService.storeTableState(this.activeTab, mergedEvent);
        this.dataSource = [];
        const isCacheExpired = dayjs().diff(activeTabState.cachedTime, 'seconds') >= CACHE_EXPIRATION_INTERVAL;
        if (!activeTabState.response || activeTabState.tableEvent !== tableEvent || isCacheExpired) {
            const allRelated = [
                ...fieldExtractor(activeTabState.tableEvent.filter?.incidentRelatedOrganizations) ?? [],
                ...fieldExtractor(activeTabState.tableEvent.filter?.incidentGovernment) ?? [],
            ];
            this.incidentsSubscription = this.incidentApiService.getIncidents({
                    query: activeTabState.tableEvent.filter.query,
                    limit: activeTabState.tableEvent.page.pageSize,
                    offset: activeTabState.tableEvent.page.pageOffset,
                    sortBy: activeTabState.tableEvent.sortBy?.columnName,
                    sortOrder: activeTabState.tableEvent.sortBy?.direction,
                    deleted: activeTabState.tableEvent.filter.query ? undefined : activeTabState.tableEvent.filter?.deleted,
                    role: activeTabState.tableEvent.filter?.incidentRole,
                    related: allRelated,
                    sourceOrganizations:
                        fieldExtractor(activeTabState.tableEvent.filter?.sourceOrganizations),
                    sourceAuthors: activeTabState.tableEvent.filter?.incidentSourceAuthors,
                    fraudTypes: fieldExtractor(activeTabState.tableEvent.filter.incidentFraudTypes),
                    incidentTypes: fieldExtractor(activeTabState.tableEvent.filter.incidentType),
                    transactionTypes: fieldExtractor(activeTabState.tableEvent.filter.incidentTransactionTypes),
                    transactionStatuses: fieldExtractor(activeTabState.tableEvent.filter.incidentTransactionStatuses),
                    amountFrom: activeTabState.tableEvent.filter?.amountRange?.from,
                    amountTo: activeTabState.tableEvent.filter?.amountRange?.to,
                    currency: activeTabState.tableEvent.filter?.amountRange?.currency,
                    balanceFrom: activeTabState.tableEvent.filter?.accountBalanceRange?.from,
                    balanceTo: activeTabState.tableEvent.filter?.accountBalanceRange?.to,
                    balanceCurrency: activeTabState.tableEvent.filter?.accountBalanceRange?.currency,
                    createDateFrom: getFormattedDateOrNull(
                        activeTabState.tableEvent.filter?.separatedDateRange?.startDate?.date,
                        DATE_TIME_SERVER_FORMAT,
                    ),
                    priority:
                        fieldExtractor(activeTabState.tableEvent.filter?.incidentPriority) as IncidentPriorityType[],

                    resolution: fieldExtractor(activeTabState.tableEvent.filter.incidentResolution),
                    status: fieldExtractor(activeTabState.tableEvent.filter.incidentStatus),
                    createDateTo: getFormattedDateOrNull(
                        activeTabState.tableEvent.filter?.separatedDateRange?.endDate?.date,
                        DATE_TIME_SERVER_FORMAT,
                    ),
                },
            )
                .pipe(rxSpinner(tableEvent.spinnerId))
                .subscribe(response => {
                    this.showCreateButton = tableEvent.spinnerId !== 'initial' || response.incidents.length > 0;
                    this.incidentFilterHolderService.storeResponse(this.activeTab, response);
                    this.buildDataSource(response);
                });
        } else {
            this.buildDataSource(activeTabState.response);
        }

    }

    private buildDataSource(response: IncidentsResponse): void {
        this.dataSource = response.incidents.map(item => {
            return <Row>{
                click: () => this.navigateToIncidentDetails(item.id),
                priority: <Column>{
                    type: ColumnType.PRIORITY,
                    text: item.priority,
                },
                resolutionStatus: <StatusColumn>{
                    type: ColumnType.STATUS,
                    text: item.resolutionStatus,
                    style: mapIncidentStatusToStyle(item.resolutionStatus),
                },
                incidentStatus: <StatusColumn>{
                    type: ColumnType.STATUS,
                    text: item.incidentStatus,
                    style: mapIncidentStatusToStyle(item.incidentStatus),
                },
                type: <Column>{
                    type: ColumnType.TEXT,
                    text: item.type || '-',
                    cellStyle: item.type?.length > ELLIPSIS_LARGE_TEXT_MAX_LENGTH && {
                        ellipsisTextLarge: true,
                    },
                    tooltip: item.type?.length > ELLIPSIS_LARGE_TEXT_MAX_LENGTH && {
                        text: item.type,
                    },
                },
                fraud_type: <Column>{
                    type: ColumnType.TEXT,
                    text: item.fraud_type,
                    cellStyle: item.fraud_type?.length > ELLIPSIS_LARGE_TEXT_MAX_LENGTH && {
                        ellipsisTextLarge: true,
                    },
                    tooltip: item.fraud_type?.length > ELLIPSIS_LARGE_TEXT_MAX_LENGTH && {
                        text: item.fraud_type,
                    },
                },
                created_date: <Column>{
                    type: ColumnType.DATE_TIME_SECONDS,
                    sortable: true,
                    text: item.created_date,
                },
                related: <Column>{
                    type: ColumnType.TEXT,
                    text: item.related?.length && item.related[0] || '-',
                    cellStyle: item.related?.join('\n')?.length > ELLIPSIS_LARGE_TEXT_MAX_LENGTH && {
                        ellipsisTextLarge: true,
                    },
                    tooltip: item.related?.join('\n')?.length > ELLIPSIS_MEDIUM_TEXT_MAX_LENGTH && {
                        text: item.related?.join('\n'),
                    },
                },
                source_organization: <Column>{
                    type: ColumnType.TEXT,
                    text: item.source_organization || '-',
                    cellStyle: item.source_organization?.length > ELLIPSIS_MEDIUM_TEXT_MAX_LENGTH && {
                        ellipsisTextMedium: true,
                    },
                    tooltip: item.source_organization?.length > ELLIPSIS_MEDIUM_TEXT_MAX_LENGTH && {
                        text: item.source_organization,
                    },
                },
                source_author: <Column>{
                    type: ColumnType.TEXT,
                    text: item.source_author,
                },
                id: <Column>{
                    type: ColumnType.TEXT,
                    text: item.id,
                    cellStyle: {
                        ellipsisTextLarge: true,
                    },
                },
            };
        });
    }

    private navigateToIncidentDetails(id: string): void {
        this.routerService.navigateToTab<IncidentsTab>(`${Path.INCIDENTS}/${id}`, this.activeTab);
    }
}

export interface IncidentTabInfo {
    tableEvent: TableEvent
    response?: IncidentsResponse;
    cachedTime: dayjs.Dayjs;
}

export declare type IncidentsTab =
    'tabAllIncidents'
    | 'tabMyIncidents'
    | 'tabRelatedIncidents'
    | 'tabOrganizationsIncidents';
